import * as Sentry from "@sentry/react";
import {captureError} from "@/config/sentry";

/**
 * A test component for Sentry that allows triggering test errors manually
 * Only visible in development and local environments
 */
function SentryTest() {
  const throwTestError = () => {
    try {
      console.log("Triggering test error for Sentry from button click");
      
      // Generate a unique ID to track this specific error
      const errorId = `test-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      
      // Log Sentry status for debugging
      console.log("Sentry Status:", {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENV,
        isDsnValid: Boolean(import.meta.env.VITE_SENTRY_DSN),
      });
      
      // Create a more detailed error for testing
        const testError = new Error(`Intentional Sentry UI test - ID: ${Math.random().toString(36).substring(2, 8)}`);
      testError.name = "SentryTestError";
      
      // Add a stack trace manually if needed
      console.error("Test error object:", testError);
      
      // Throw the error
      throw testError;
    } catch (e) {
      // Log capture attempt
      console.log(`Attempting to send error to Sentry: ${e.message}`);
      
      // First, try the direct approach with Sentry.captureException
      console.log("Method 1: Using direct Sentry.captureException");
      Sentry.captureException(e);
      
      // Then try with our helper function with more context
      console.log("Method 2: Using captureError helper");
      captureError(e, {
        tags: {
          component: "SentryTest",
          errorId: e.message.match(/ID: ([a-z0-9-]+)/)?.[1] || "unknown",
          method: "button-click"
        },
        extra: {
          location: "SentryTest component",
          browser: navigator.userAgent,
          timestamp: new Date().toISOString(),
          type: "manual-test"
        },
        level: "info"
      });
      
      // Add a third fallback method with manual scope
      console.log("Method 3: Using withScope fallback");
      Sentry.withScope(scope => {
        scope.setLevel("info");  // Set custom level
        scope.setTag("component", "SentryTest");
        scope.setTag("errorId", e.message.match(/ID: ([a-z0-9-]+)/)?.[1] || "unknown");
        scope.setTag("method", "fallback");
        scope.setExtra("location", "SentryTest component");
        scope.setExtra("timestamp", new Date().toISOString());
        
        // Capture with the enhanced scope
        Sentry.captureException(e);
      });
      
      // Show feedback to user
      alert(`Error sent to Sentry! Error ID: ${e.message.match(/ID: ([a-z0-9-]+)/)?.[1] || "unknown"}\nCheck your dashboard and browser console.`);
    }
  };

  // Display helpful information about current Sentry configuration
  const sentryStatus = import.meta.env.VITE_SENTRY_DSN ? "✓ Set" : "✗ Missing";
  const envStatus = import.meta.env.VITE_ENV || "not set";

  return (
    <div style={{ 
      position: 'fixed', 
      bottom: '20px', 
      right: '20px', 
      background: '#f0f0f0',
      padding: '10px', 
      borderRadius: '5px',
      border: '1px solid #ddd',
      zIndex: 10000,
      display: ['local', 'dev'].includes(import.meta.env.VITE_ENV) ? 'block' : 'none'
    }}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Sentry Testing</h4>
      <button 
        onClick={throwTestError}
        style={{
          background: '#ff6b6b',
          color: 'white',
          border: 'none',
          padding: '5px 10px',
          borderRadius: '3px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
      >
        Test Sentry Error
      </button>
      <div style={{ fontSize: '10px', marginTop: '4px', color: '#666' }}>
        DSN: {sentryStatus} | Env: {envStatus}
      </div>
    </div>
  );
}

export default SentryTest; 